import { useState, useContext, useEffect } from "react";
import { useQuery } from "react-query";
import {
  Typography,
  Paper,
  Stack,
  Container,
  Box,
  alpha,
  useTheme,
} from "@mui/material";
import {
  People as PeopleIcon,
  Search as SearchIcon,
  Add as AddIcon,
} from "@mui/icons-material";

import { QK } from "../../base/qk";
import { getCandidates, getCandidatesbyId } from "../../api/services/candidate";
import MyContext from "../../context/MyContext";
import { CandidateTabHeaders } from "./CandidateScreen.config";

import DynamicButton from "../../components/DynamicButton/DynamicButton";
import ListTable from "../../components/ListTable/ListTable";
import TableWithNav from "../../components/TableWIthNav/TableWithNav";
import SearchBar from "../../components/Search/SearchBar";

const Candidates = () => {
  const theme = useTheme();
  const [clickedCandidateId, setClickedCandidateId] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { locationID } = useContext(MyContext);

  // Reset clicked candidate when search changes
  useEffect(() => {
    setClickedCandidateId(null);
  }, [search]);

  const { data: candidates, isLoading: isListLoading } = useQuery(
    [QK.candidates, currentPage, search, locationID],
    () => getCandidates(currentPage, search, locationID),
    {
      enabled: true,
      keepPreviousData: true,
    }
  );

  const { data: candidate, isLoading: isDetailLoading } = useQuery(
    [QK.candidate, clickedCandidateId],
    () => getCandidatesbyId(clickedCandidateId),
    {
      enabled: !!clickedCandidateId,
    }
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <Container maxWidth={false} sx={{ py: 0, px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
        {/* LEFT COLUMN: Candidate List & Search */}
        <Box sx={{ flex: "1 1 280px", minWidth: "260px", maxWidth: "360px" }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 2,
              bgcolor: "#FFFFFF",
              borderColor: "#E2E8F0",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "6px",
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PeopleIcon
                    sx={{ color: theme.palette.primary.main, fontSize: 16 }}
                  />
                </Box>
                <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                  Candidates
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: "#F1F5F9",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: "12px",
                    color: "#475467",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                  }}
                >
                  {candidates?.count || 0}
                </Typography>
              </Box>
              <DynamicButton
                route="/candidate/create"
                text="Add"
                variant="contained"
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "6px",
                  bgcolor: "#2469F0",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.7rem",
                  "&:hover": {
                    bgcolor: "#1A5BD4",
                  },
                }}
              />
            </Stack>

            <SearchBar
              placeholder="Search candidates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              borderColor: "#E2E8F0",
            }}
          >
            <ListTable
              getCandidateId={setClickedCandidateId}
              data={candidates?.results}
              handlePageChange={handlePageChange}
              currentPage={currentPage}
              totalRecords={candidates?.count}
              activeId={clickedCandidateId}
              isLoading={isListLoading}
            />
          </Paper>
        </Box>

        {/* RIGHT COLUMN: Candidate Details & Tabs */}
        <Box sx={{ flex: "2 1 500px", minWidth: "350px" }}>
          <TableWithNav
            data={candidate?.data}
            isLoading={isDetailLoading}
            tabHeader={CandidateTabHeaders.map((tab) => ({
              ...tab,
              clickedId: clickedCandidateId,
            }))}
            clickedId={clickedCandidateId}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default Candidates;